import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../hooks/useAdmin';
import { subscribeToPendingQuestCompletionsCount } from '../../services/firestoreService';
import { subscribeToPendingQuestRequestsCount } from '../../services/adminService';
import useMouseGlow from '../../hooks/useMouseGlow';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';
import AdminSidebar from './AdminSidebar';
import AdminUserManager from './AdminUserManager';
import AdminQuestManager from './AdminQuestManager';
import AdminQuestApproval from './AdminQuestApproval';
import AdminQuestCompletion from './AdminQuestCompletion';
import AdminStats from './AdminStats';
import '../../admin.css';

export default function AdminPortal() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('stats');
  
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [pendingCompletionsCount, setPendingCompletionsCount] = useState(0);

  const {
    isAdmin,
    adminLoading,
    isAuthenticated,
    users,
    availableQuests,
    questRequests,
    pendingQuestRequests,
    usersLoading,
    questsLoading,
    requestsLoading,
    error,
    loadUsers,
    loadQuests,
    loadQuestRequests
  } = useAdmin();

  // Mouse glow for admin modals
  useMouseGlow('.modal');

  // Load data when tab becomes active
  useEffect(() => {
    if (!isAdmin) return;
    
    switch (activeTab) {
      case 'users':
        // User management now uses email search instead of loading all users
        break;
      case 'quests':
        loadQuests();
        break;
      case 'approval':
        loadQuestRequests();
        loadUsers(); // Also load users for displaying usernames
        break;
      case 'stats':
        // Stats needs users and quests data
        loadUsers();
        loadQuests();
        break;
      default:
        break;
    }
  }, [activeTab, isAdmin, loadUsers, loadQuests, loadQuestRequests]);

  // Lightweight count-only subscriptions for sidebar badges (no doc deserialization)
  useEffect(() => {
    if (!isAdmin) return;
    const unsubRequests = subscribeToPendingQuestRequestsCount(setPendingRequestsCount);
    const unsubCompletions = subscribeToPendingQuestCompletionsCount(setPendingCompletionsCount);
    return () => { unsubRequests(); unsubCompletions(); };
  }, [isAdmin]);

  // Show loading while checking authentication
  if (adminLoading) {
    return (
      <div className="admin-portal-loading">
        <div className="loading-content">
          <FontAwesomeIcon icon={ICONS.SPINNER} spin />
          <p>{t('admin.checkingPrivileges')}</p>
        </div>
      </div>
    );
  }

  // Show access denied if not authenticated or not admin
  if (!isAuthenticated || !isAdmin) {
    return (
      <div className="admin-portal-denied">
        <div className="denied-content">
          <FontAwesomeIcon icon={ICONS.LOCK} />
          <h2>{t('admin.accessDenied')}</h2>
          <p>{t('admin.noPermission')}</p>
          {!isAuthenticated && <p>{t('admin.pleaseLogin')}</p>}
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'stats', label: t('admin.overview'), icon: ICONS.CHART_BAR },
    { id: 'users', label: t('admin.userManagement'), icon: ICONS.USERS },
    { id: 'quests', label: t('admin.quests'), icon: ICONS.TARGET },
    { id: 'approval', label: t('admin.questApproval.title'), icon: ICONS.CLIPBOARD_LIST, count: pendingRequestsCount > 0 ? pendingRequestsCount : undefined },
    { id: 'completion', label: 'Completion Review', icon: ICONS.CHECK_CIRCLE, count: pendingCompletionsCount > 0 ? pendingCompletionsCount : undefined }
  ];

  return (
    <div className="admin-portal">
      <AdminSidebar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      <main className="admin-main">
        {error && (
          <div className="admin-error">
            <FontAwesomeIcon icon={ICONS.EXCLAMATION_TRIANGLE} />
            <span>{error}</span>
          </div>
        )}

        <div className="admin-content">
          {activeTab === 'stats' && (
            <AdminStats
              users={users}
              availableQuests={availableQuests}
              usersLoading={usersLoading}
              questsLoading={questsLoading}
            />
          )}

          {activeTab === 'users' && (
            <AdminUserManager />
          )}

          {activeTab === 'quests' && (
            <AdminQuestManager
              availableQuests={availableQuests}
              loading={questsLoading}
            />
          )}

          {activeTab === 'approval' && (
            <AdminQuestApproval
              questRequests={questRequests}
              pendingQuestRequests={pendingQuestRequests}
              requestsLoading={requestsLoading}
              users={users}
            />
          )}

          {activeTab === 'completion' && (
            <AdminQuestCompletion
              users={users}
            />
          )}
        </div>
      </main>
    </div>
  );
} 